import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Shield, TrendingUp, Calculator, FileText, CheckCircle, ArrowRight, Target } from 'lucide-react';
import SEO, { SEOPages } from '../components/SEO';

const PME = () => {
  const pmeServices = [
    {
      icon: Shield,
      title: "Prévoyance professionnelle",
      description: "Solutions LPP adaptées aux besoins de votre entreprise et vos collaborateurs",
      benefits: ["Plans de prévoyance sur mesure", "Optimisation des coûts", "Gestion administrative simplifiée"]
    },
    {
      icon: Calculator,
      title: "Optimisation fiscale entreprise",
      description: "Stratégies fiscales pour réduire la charge de votre entreprise",
      benefits: ["Planification fiscale pluriannuelle", "Optimisation société/dividendes", "Conseil en restructuration"]
    },
    {
      icon: Building2,
      title: "Création d'entreprise",
      description: "Accompagnement complet dans la création de votre société",
      benefits: ["Choix de la forme juridique", "Optimisation capital/financement", "Mise en place prévoyance"]
    },
    {
      icon: Users,
      title: "Succession d'entreprise",
      description: "Planification et accompagnement de la transmission d'entreprise",
      benefits: ["Évaluation d'entreprise", "Stratégies de transmission", "Optimisation fiscale succession"]
    },
    {
      icon: TrendingUp,
      title: "Financement entreprise",
      description: "Solutions de financement pour développer votre activité",
      benefits: ["Crédit d'exploitation", "Financement investissements", "Négociation bancaire"]
    },
    {
      icon: FileText,
      title: "Assurances entreprise",
      description: "Couverture complète des risques liés à votre activité",
      benefits: ["RC professionnelle", "Perte d'exploitation", "Assurance crédit"]
    }
  ];

  const businessStructures = [
    {
      type: "Raison individuelle",
      min_capital: "Aucun",
      taxation: "Impôt personnel",
      liability: "Illimitée",
      best_for: "Petites activités"
    },
    {
      type: "SARL", 
      min_capital: "CHF 20'000",
      taxation: "Impôt société",
      liability: "Limitée",
      best_for: "PME classiques"
    },
    {
      type: "SA",
      min_capital: "CHF 100'000", 
      taxation: "Impôt société",
      liability: "Limitée",
      best_for: "Grandes entreprises"
    }
  ];

  const taxOptimizations = [
    { strategy: "Optimisation salaire/dividendes", saving: "15-25%", complexity: "Moyenne" },
    { strategy: "Amortissements accélérés", saving: "Report d'impôt", complexity: "Faible" },
    { strategy: "Provisions et réserves", saving: "10-20%", complexity: "Élevée" },
    { strategy: "Rachats LPP dirigeants", saving: "Jusqu'à 40%", complexity: "Moyenne" },
    { strategy: "Holding et restructuration", saving: "20-35%", complexity: "Élevée" }
  ];

  return (
    <>
      <SEO {...SEOPages.pme} />
      <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          role="img"
          aria-label="Solutions financières PME suisses - prévoyance professionnelle LPP et optimisation entreprise"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzU3MzQzMDYyfDA&ixlib=rb-4.1.0&q=85)',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold heading-display text-white mb-6">
              PME & Indépendants : <span className="text-orange-300">optimisez</span> votre entreprise
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Solutions spécialisées pour entrepreneurs, indépendants et PME suisses. 
              Optimisation fiscale, prévoyance professionnelle et accompagnement business.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-8 py-4 flex items-center justify-center">
                Analyser mon entreprise
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link to="/contact" className="btn-glass text-lg px-8 py-4 flex items-center justify-center">
                <Calculator className="mr-2" size={20} />
                Demander une offre assurance PME
              </Link>
            </div>

            {/* Statistiques PME */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-orange-400 mb-2">25%</div>
                <div className="text-gray-300">Économie fiscale moyenne</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-green-400 mb-2">Expertise</div>
                <div className="text-gray-300">Conseil sur mesure pour PME</div>
              </div>
              <div className="glass p-6 rounded-2xl">
                <div className="text-3xl font-bold text-white mb-2">48h</div>
                <div className="text-gray-300">Délai de réponse moyen</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services PME */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Nos services pour entreprises
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Accompagnement complet pour tous vos besoins d'entrepreneur
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pmeServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="glass p-8 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-300 mb-6">{service.description}</p>
                  
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-300">
                        <CheckCircle size={16} className="text-green-400 mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Formes juridiques */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Choisir la bonne forme juridique
            </h2>
            <p className="text-xl text-gray-300">
              Comparaison des principales structures d'entreprise en Suisse
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {businessStructures.map((structure, index) => (
              <div key={index} className="glass p-8 rounded-3xl">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Building2 size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{structure.type}</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="glass p-4 rounded-xl">
                    <div className="text-sm text-gray-300 mb-1">Capital minimum</div>
                    <div className="text-white font-semibold">{structure.min_capital}</div>
                  </div>
                  <div className="glass p-4 rounded-xl">
                    <div className="text-sm text-gray-300 mb-1">Taxation</div>
                    <div className="text-white font-semibold">{structure.taxation}</div>
                  </div>
                  <div className="glass p-4 rounded-xl">
                    <div className="text-sm text-gray-300 mb-1">Responsabilité</div>
                    <div className="text-white font-semibold">{structure.liability}</div>
                  </div>
                  <div className="glass p-4 rounded-xl">
                    <div className="text-sm text-gray-300 mb-1">Idéal pour</div>
                    <div className="text-white font-semibold">{structure.best_for}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Optimisations fiscales */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Stratégies d'optimisation fiscale
            </h2>
            <p className="text-xl text-gray-300">
              Les principales techniques pour réduire la charge fiscale de votre entreprise
            </p>
          </div>
          
          <div className="space-y-6 max-w-4xl mx-auto">
            {taxOptimizations.map((optimization, index) => (
              <div key={index} className="glass p-6 rounded-2xl">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{optimization.strategy}</h3>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-300">Économie potentielle</div>
                    <div className="text-lg font-bold text-green-400">{optimization.saving}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-300">Complexité</div>
                    <div className={`text-sm font-semibold ${
                      optimization.complexity === 'Faible' ? 'text-green-400' :
                      optimization.complexity === 'Moyenne' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {optimization.complexity}
                    </div>
                  </div>
                  <div className="text-center">
                    <Link to="/contact" className="btn-glass text-sm px-4 py-2">
                      En savoir plus
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prévoyance professionnelle */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Prévoyance professionnelle optimisée
            </h2>
            <p className="text-xl text-gray-300">
              En tant qu'entrepreneur, vous pouvez optimiser votre prévoyance professionnelle 
              pour réduire vos impôts et sécuriser votre retraite
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6 mb-12">
              {[
                { title: "Plans de prévoyance flexibles", desc: "Adaptés à la fluctuation de vos revenus" },
                { title: "Rachats LPP optimisés", desc: "Stratégie de rachats pour maximiser les déductions" },
                { title: "Prévoyance des dirigeants", desc: "Solutions spécifiques aux administrateurs" }
              ].map((item, index) => (
                <div key={index} className="glass p-6 rounded-2xl">
                  <div className="flex items-start space-x-4">
                    <CheckCircle size={20} className="text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                      <p className="text-gray-300 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <Link to="/contact" className="btn-glass btn-primary px-8 py-3">
                Optimiser ma prévoyance
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Processus d'accompagnement */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Notre accompagnement PME
            </h2>
            <p className="text-xl text-gray-300">
              Une approche personnalisée en 5 étapes
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: "1", title: "Audit", desc: "Analyse complète de votre situation" },
              { step: "2", title: "Stratégie", desc: "Définition d'objectifs et solutions" },
              { step: "3", title: "Planification", desc: "Roadmap détaillée de mise en œuvre" },
              { step: "4", title: "Exécution", desc: "Implémentation des optimisations" },
              { step: "5", title: "Suivi et Coaching", desc: "Accompagnement et ajustements" }
            ].map((item, index) => (
              <div key={index} className="glass p-6 text-center flex flex-col items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mb-4">
                  <span className="text-lg font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm text-center">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="glass p-12 rounded-3xl text-center">
            <h2 className="text-4xl font-bold heading-display text-white mb-6">
              Prêt à optimiser votre entreprise ?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Nos experts PME vous accompagnent pour maximiser la performance 
              fiscale et financière de votre entreprise.
            </p>
            <div className="flex justify-center">
              <Link to="/contact" className="btn-glass btn-primary text-lg px-12 py-4">
                Consulter un expert PME
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default PME;